import type { createPublicClient, Event as PolyEvent } from '@polymarket/client';

type PolyClient = ReturnType<typeof createPublicClient>;
export type ListEventsRequest = NonNullable<Parameters<PolyClient['listEvents']>[0]>;
export type FetchEventRequest = Parameters<PolyClient['fetchEvent']>[0];

export type LiveUpdate = {
  topic: 'market';
  type: string;
  payload: { tokenId: string; [k: string]: unknown };
};

export function isEventClosed(event: PolyEvent | null | undefined): boolean {
  return !!event?.state?.closed;
}

function longestCommonPrefix(strings: readonly string[]): string {
  if (strings.length === 0) return '';
  let prefix = strings[0];
  for (let i = 1; i < strings.length && prefix; i++) {
    while (!strings[i].startsWith(prefix)) {
      prefix = prefix.slice(0, -1);
      if (!prefix) return '';
    }
  }
  while (prefix.length > 0) {
    const last = prefix.charAt(prefix.length - 1);
    if (!/\w/.test(last)) break;
    const splitsWord = strings.some((s) => /\w/.test(s.charAt(prefix.length)));
    if (!splitsWord) break;
    prefix = prefix.slice(0, -1);
  }
  return prefix;
}

function longestCommonSuffix(strings: readonly string[]): string {
  if (strings.length === 0) return '';
  let suffix = strings[0];
  for (let i = 1; i < strings.length && suffix; i++) {
    while (!strings[i].endsWith(suffix)) {
      suffix = suffix.slice(1);
      if (!suffix) return '';
    }
  }
  while (suffix.length > 0) {
    const first = suffix.charAt(0);
    if (!/\w/.test(first)) break;
    const splitsWord = strings.some((s) => /\w/.test(s.charAt(s.length - suffix.length - 1)));
    if (!splitsWord) break;
    suffix = suffix.slice(1);
  }
  return suffix;
}

function shortQuestionFor(question: string, prefix: string, suffix: string): string {
  if (!prefix && !suffix) return question;
  const trimmed = question.slice(prefix.length, question.length - suffix.length).trim();
  return trimmed || question;
}

export function sortEventMarkets(event: PolyEvent, options?: { forceSort?: boolean }): PolyEvent {
  const markets = event?.markets;
  if (!markets || markets.length === 0) return event;

  const isSports = event?.sports?.gameId != null;
  const skipSort = markets.length < 2 || (isSports && !options?.forceSort);
  const ordered = skipSort
    ? markets
    : [...markets].sort((a, b) => {
        const ap = Number(a?.outcomes?.yes?.price ?? 0);
        const bp = Number(b?.outcomes?.yes?.price ?? 0);
        return bp - ap;
      });

  const questions = ordered.map((m) => m.question ?? '');
  const prefix = ordered.length > 1 ? longestCommonPrefix(questions) : '';
  const suffix = ordered.length > 1 ? longestCommonSuffix(questions) : '';

  const enriched = ordered.map((m, i) => ({
    ...m,
    shortQuestion: shortQuestionFor(questions[i], prefix, suffix),
  }));

  return { ...event, markets: enriched as typeof markets };
}

export const ALLOWED_SORT = [
  'volume24hr',
  'volume',
  'liquidity',
  'featuredOrder',
  'startDate',
  'endDate',
  'competitive',
] as const;
export const ALLOWED_STATUS = ['active', 'resolved'] as const;

export type SortValue = (typeof ALLOWED_SORT)[number];
export type StatusValue = (typeof ALLOWED_STATUS)[number];

export const DEFAULT_SORT: SortValue = 'volume24hr';
export const DEFAULT_STATUS: StatusValue = 'active';
export const PAGE_SIZE = 18;

export function parseSort(value: string | null | undefined): SortValue {
  return (ALLOWED_SORT as readonly string[]).includes(value ?? '') ? (value as SortValue) : DEFAULT_SORT;
}

export function parseStatus(value: string | null | undefined): StatusValue {
  return (ALLOWED_STATUS as readonly string[]).includes(value ?? '') ? (value as StatusValue) : DEFAULT_STATUS;
}

export type BuildRequestInput = {
  sort: SortValue;
  status: StatusValue;
  slugId?: string;
  search?: string;
  pageSize?: number;
};

export function buildListEventsRequest(input: BuildRequestInput): ListEventsRequest {
  const { sort, status, slugId, search, pageSize = PAGE_SIZE } = input;

  const request: ListEventsRequest = {
    pageSize,
    ascending: false,
    closed: status === 'resolved',
  };

  if (sort === 'featuredOrder') {
    request.featuredOrder = true;
  } else {
    request.order = sort;
  }

  if (slugId === 'featured') {
    request.featured = true;
    request.featuredOrder = true;
  } else if (slugId && slugId !== 'trending' && slugId !== 'all') {
    request.tagSlug = slugId;
  }

  const trimmedSearch = search?.trim();
  if (trimmedSearch) {
    request.titleSearch = trimmedSearch;
  }

  return request;
}

export function extractTokenIds(events: readonly PolyEvent[]): string[] {
  const ids = new Set<string>();
  for (const event of events) {
    for (const market of event.markets ?? []) {
      market.outcomes &&
        Object.values(market.outcomes).forEach((o) => {
          if (o.tokenId) ids.add(o.tokenId);
        });
    }
  }
  return [...ids];
}
